import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Spinner } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("it@accountingfarm.com");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F172A] text-white relative overflow-hidden p-12">
        <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(#fff_1px,transparent_1px),linear-gradient(90deg,#fff_1px,transparent_1px)] [background-size:32px_32px]" />
        <div className="relative z-10 flex flex-col h-full justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#2563EB] rounded-sm flex items-center justify-center">
              <ShieldCheck size={20} weight="bold" />
            </div>
            <div>
              <div className="font-display font-semibold tracking-tight">HRMS Suite</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Multi-business platform</div>
            </div>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#FAB005] mb-4">
              One platform · Five businesses
            </div>
            <h1 className="font-display text-5xl tracking-tight font-medium leading-[1.05]">
              Run HR for every <br /> entity from a <br />
              <span className="text-[#FAB005]">single control room.</span>
            </h1>
            <p className="mt-6 text-sm text-white/70 max-w-md leading-relaxed">
              Centralised onboarding, attendance, leave, payroll and recruitment across ACCOUNTINGFARM, TRANQUIL BUSINESS, CA JAY K SHAH ASSOCIATES, MANUSHI FINSERV and FINQUILL LIMITED.
            </p>
          </div>

          <div className="grid grid-cols-5 gap-3 text-[10px] uppercase tracking-[0.16em] text-white/60 font-mono">
            {["AF", "TB", "CJS", "MF", "FL"].map((c) => (
              <div key={c} className="border border-white/15 py-3 text-center">{c}</div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#64748B] mb-3">
            Sign in to continue
          </div>
          <h2 className="font-display text-3xl tracking-tight font-medium text-[#0F172A]">
            Welcome back.
          </h2>
          <p className="text-sm text-[#475569] mt-1">
            Use your work email to access the dashboard.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 rounded-sm border-border focus-visible:ring-2 focus-visible:ring-[#2563EB]"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 rounded-sm border-border focus-visible:ring-2 focus-visible:ring-[#2563EB]"
                data-testid="password-input"
              />
            </div>

            {err && (
              <div className="text-xs text-[#F54236] border border-[#F54236]/30 bg-[#F54236]/5 px-3 py-2 rounded-sm" data-testid="login-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm uppercase tracking-wider font-medium"
              data-testid="login-submit"
            >
              {loading && <Spinner size={14} className="mr-2 animate-spin" />}
              Sign in
            </Button>

            <div className="flex items-center justify-between text-xs text-[#475569]">
              <Link to="/forgot-password" className="hover:text-[#2563EB]" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </form>

          <div className="mt-10 pt-6 border-t border-border">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#64748B] mb-2">
              Need help?
            </div>
            <div className="text-xs text-[#475569] leading-relaxed">
              Use your work email and password to sign in.
              Contact your IT administrator if you don&rsquo;t have an account yet.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
